import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCategoryLable, getStatusLable, getPriorityLable, getMessageState, getTicketSeenState, TicketApiModel, TicketInfoApiModel } from "./common";
import MessageBox from "./message_box";

const API_BASE_URL = 'https://api.neontradepro.com';
const MEDIA_BASE_URL = 'https://neontradepro.com/media';

export default function TicketDetails({ticket_id, isAdmin = false}: {ticket_id: string, isAdmin?: boolean}){
    const [ticket, setTicket] = useState<TicketApiModel|null>(null);
    const [ticketInfo, setTicketInfo] = useState<TicketInfoApiModel|null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [closeLoading, setCloseLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const navigate = useNavigate();

    const token = localStorage.getItem('token');
    const theme = localStorage.getItem('theme');

    function getMessageChip(message: TicketInfoApiModel["messages"][number]) {
        const state = getMessageState(message);
        switch (state) {
            case "new":
                return <Chip size="small" label="جدید" className="ticket-chip ticket-chip-new" />;
            case "answered":
                return <Chip size="small" label="پاسخ داده شده" className="ticket-chip ticket-chip-answered" />;
            case "seen":
                return <Chip size="small" label="دیده شده" className="ticket-chip ticket-chip-seen" />;
            default:
                return null;
        }
    }

    async function handleCloseTicket() {
        if (!token || !ticket || closeLoading) return;
        setCloseLoading(true);
        setError('');

        const requests = [
            { url: `${API_BASE_URL}/tickets/${ticket.id}/close`, options: { method: 'POST' } },
            { url: `${API_BASE_URL}/tickets/${ticket.id}/close`, options: { method: 'PATCH' } },
            { url: `${API_BASE_URL}/tickets/${ticket.id}`, options: { method: 'PATCH', body: JSON.stringify({ status: 'closed' }) } },
        ];

        try {
            let success = false;
            for (const request of requests) {
                const response = await fetch(request.url, {
                    ...request.options,
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        ...(request.options.body ? { 'Content-Type': 'application/json' } : {}),
                    },
                });

                if (response.ok) {
                    success = true;
                    break;
                }
            }

            if (!success) {
                throw new Error('بستن تیکت از سمت سرور انجام نشد.');
            }

            setTicket({
                ...ticket,
                status: 'closed',
                closed_at: Math.floor(Date.now() / 1000),
            });
            setTicketInfo(ticketInfo ? {
                ...ticketInfo,
                ticket: {
                    ...ticketInfo.ticket,
                    status: 'closed',
                    closed_at: Math.floor(Date.now() / 1000),
                }
            } : ticketInfo);
        } catch (err: any) {
            setError(err.message || 'خطا در بستن تیکت');
        } finally {
            setCloseLoading(false);
        }
    }

    function ShowTicketInfo({className}: {className?: string} = {}){
        if (!ticket) return null;
        const ticketState = getTicketSeenState(ticket);

        return <div className={clsx("ticket-side-card", className)}>
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="flex items-center">
                <button
                    className="ticket-back-button rounded-md"
                    onClick={() => navigate(isAdmin ? '/admin?tab=8' : '/dashboard/tickets')}
                >
                    <i className="material-icons p-2">arrow_forward</i>
                </button>
                <h2 className="text-2xl font-bold mr-3">تیکت #{ticket.id}</h2>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {ticketState === "new" && <Chip size="small" label="پیام جدید" className="ticket-chip ticket-chip-new" />}
                    {ticketState === "answered" && <Chip size="small" label="پاسخ داده شده" className="ticket-chip ticket-chip-answered" />}
                    {ticketState === "seen" && <Chip size="small" label="دیده شده" className="ticket-chip ticket-chip-seen" />}
                    {isAdmin && ticket.status !== "closed" && <button className="ticket-close-button" onClick={handleCloseTicket} disabled={closeLoading}>{closeLoading ? 'در حال بستن...' : 'بستن تیکت'}</button>}
                </div>
            </div>
            <p className="ticket-info-line"><strong>عنوان:</strong> {ticket.subject}</p>
            <p className="ticket-info-line"><strong>دسته‌بندی:</strong> {getCategoryLable(ticket.category)}</p>
            <p className="ticket-info-line"><strong>وضعیت:</strong> {getStatusLable(ticket.status)}</p>
            <p className="ticket-info-line"><strong>اولویت:</strong> {getPriorityLable(ticket.priority)}</p>
            <p className="ticket-info-line"><strong>ایجاد شده در:</strong> {new Date(ticket.created_at * 1000).toLocaleString('fa-IR')}</p>
            <p className="ticket-info-line"><strong>آخرین به‌روزرسانی:</strong> {ticket.updated_at ? new Date(ticket.updated_at * 1000).toLocaleString('fa-IR') : 'ندارد'}</p>
            <p className="ticket-info-line">
                <strong>کاربر: </strong>
                {ticket.user.first_name} {ticket.user.last_name} 
                <span className="ticket-secondary-text mx-1">[‎@{ticket.user.username} #{ticket.user.id}]</span>
            </p>
            {ticket.closed_at && <p className="ticket-info-line"><strong>بسته شده در:</strong> {new Date(ticket.closed_at * 1000).toLocaleString('fa-IR')}</p>}
        </div>
    }

    function downloadMedia(url: string, fileName: string) {
        fetch(url)
            .then(res => res.blob())
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                a.click();
                window.URL.revokeObjectURL(url);
            });
    }

    useEffect(() => {
        async function fetchTicketInfo() {
            if (!token) return;
            setIsLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}/tickets/${ticket_id}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    const errorMessage = Array.isArray(errorData.detail)
                        ? errorData.detail.map((item: any) => item?.msg).filter(Boolean).join('، ')
                        : errorData.detail;
                    throw new Error(errorMessage || 'خطا در دریافت اطلاعات تیکت');
                }

                const data = await response.json();
                if (!data?.ticket) {
                    throw new Error('اطلاعات تیکت نامعتبر است.');
                }
                setTicketInfo({
                    ticket: data.ticket,
                    messages: Array.isArray(data.messages) ? data.messages : []
                });
                setTicket(data.ticket);
            } catch (err: any) {
                console.error('Failed to fetch ticket info:', err);
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        }

        fetchTicketInfo();
    }, [ticket_id, token]);

    if (error) return <Alert severity="error">{error}</Alert>;
    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
    if (!ticket || !ticketInfo) return <Alert severity="warning">اطلاعات تیکت برای نمایش در دسترس نیست.</Alert>;

    return <div className="ticket-page md:flex md:w-full md:gap-5">
        <ShowTicketInfo className="w-full md:w-1/3 mb-3"/>
        <div className="w-full md:w-2/3 flex flex-col">
            <div
                className={clsx(
                    "ticket-thread flex flex-col border p-4 rounded-lg",
                    theme === "dark"? "border-white/20": "border-black/10"
                )}
            >
                {ticketInfo.messages.map((message, index) => (
                    <div
                        key={index}
                        className={clsx(
                            "mb-3 max-w-[85%] w-fit",
                            message.is_admin? "self-end": "self-start"
                        )}
                    >
                        <div
                            className={clsx(
                                "ticket-message-bubble p-4 rounded-lg whitespace-pre-line",
                                message.is_admin? "ticket-message-admin": "ticket-message-user"
                            )}>
                            <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                                <span className="ticket-bubble-author">{message.is_admin ? "پشتیبانی" : "کاربر"}</span>
                                {getMessageChip(message)}
                            </div>
                            {message.message}
                            {(message.media || []).map((media, mediaIndex) => (
                                <div
                                    key={mediaIndex}
                                    className="ticket-media-item text-sm cursor-pointer hover:underline w-fit p-1 rounded mt-2"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        downloadMedia(`${MEDIA_BASE_URL}/${media.path}`, media.file_name);
                                    }}
                                >
                                    {media.file_name.slice(0, 30)}{media.file_name.length > 30 ? '...' : ''} ({(media.file_size / 1024).toFixed(2)} KB)
                                </div>
                            ))}
                        </div>
                        <span className="ticket-secondary-text text-xs mt-1 inline-block">{new Date(message.sent_at * 1000).toLocaleString('fa-IR')}</span>
                    </div>
                ))}
            </div>
            {isAdmin ? (
                ticket.status !== "closed"
                    ? <MessageBox className="px-2" ticketId={ticket.id} theme={theme || 'dark'} />
                    : <Alert sx={{ mt: 2 }} severity="info">این تیکت بسته شده است.</Alert>
            ) : (
                ticket.status !== "closed"
                    ? <MessageBox className="px-2" ticketId={ticket.id} theme={theme || 'dark'} />
                    : <Alert sx={{ mt: 2 }} severity="info">این تیکت بسته شده و کاربر دیگر نمی‌تواند پیامی ارسال کند.</Alert>
            )}
        </div>
    </div>;
}
